import React from "react";
import type { PackagePayload, ResolveConflict } from "../../../services/packages";
import { describePackagePermission } from "../../../utils/packagePermissions";
import styles from "./InstallPermissionsDialog.module.css";

export interface InstallPermissionsDialogProps {
  pkg: PackagePayload;
  conflicts: ResolveConflict[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Label for the confirm button. Defaults to the per-dataflow wording used by
   * the Node Catalog drawer; the /catalog/nodes page passes the all-projects
   * wording, since that surface writes the user's defaults rather than one
   * dataflow's lockfile.
   */
  confirmLabel?: string;
  /** The confirm button's label while the action runs. */
  busyLabel?: string;
}

export const InstallPermissionsDialog: React.FC<InstallPermissionsDialogProps> = ({
  pkg,
  conflicts,
  busy,
  onCancel,
  onConfirm,
  confirmLabel = "Add to project",
  busyLabel = "Adding…",
}) => {
  const hasConflicts = conflicts.length > 0;
  // The packages it names in `dependencies.packages` are added with it.
  const packageDeps = Object.entries(pkg.dependencies.packages ?? {});
  const pythonDeps = Object.entries(pkg.dependencies.python);
  const jsDeps = Object.entries(pkg.dependencies.js);

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      {/* A stable hook for e2e captures: the module class is hashed. */}
      <div className={styles.modal} data-curio-add-review="true">
        <h2 className={styles.title}>Add &quot;{pkg.name}&quot;</h2>
        <p className={styles.subtitle}>
          {pkg.publisher} · v{pkg.version}
        </p>

        {pkg.permissions.length > 0 && (
          <>
            <p className={styles.depsTitle}>Permissions requested</p>
            <ul className={styles.permList}>
              {pkg.permissions.map((perm) => {
                const meaning = describePackagePermission(perm);
                return (
                  <li key={perm} className={styles.permItem}>
                    <span className={styles.permIcon}>●</span>
                    {perm}
                    {meaning ? ` — ${meaning}` : ""}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {(packageDeps.length > 0 || pythonDeps.length > 0 || jsDeps.length > 0) && (
          <div className={styles.depsBox}>
            <p className={styles.depsTitle}>Dependencies</p>
            {packageDeps.map(([name, range]) => (
              <div key={`package:${name}`} className={styles.depRow}>
                <code>package · {name}</code>
                <code>{range}</code>
              </div>
            ))}
            {pythonDeps.map(([pkg, range]) => (
              <div key={`py:${pkg}`} className={styles.depRow}>
                <code>python · {pkg}</code>
                <code>{range}</code>
              </div>
            ))}
            {jsDeps.map(([pkg, range]) => (
              <div key={`js:${pkg}`} className={styles.depRow}>
                <code>js · {pkg}</code>
                <code>{range}</code>
              </div>
            ))}
          </div>
        )}

        {hasConflicts && (
          <div className={styles.conflictsBox}>
            <p className={styles.conflictTitle}>Dependency conflicts with installed packages</p>
            {conflicts.map((c) => (
              <div key={c.package}>
                <p>
                  <strong>{c.package}</strong>
                </p>
                <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
                  {c.ranges.map((r) => (
                    <li key={r.packageDir}>
                      <code>{r.packageDir}</code>: <code>{r.range}</code>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <p className={styles.conflictHint}>
              Remove one of the conflicting packages before adding this one.
            </p>
          </div>
        )}

        {busy && (
          <div className={styles.installProgress}>
            <div className={styles.installProgressBar}>
              <div className={styles.installProgressStripe} />
            </div>
            <div className={styles.installProgressHint}>
              Installing package + pip-installing its Python deps. Heavy
              ML packages (torch, transformers) can take several minutes
              on a cold env, so keep this window open.
            </div>
          </div>
        )}

        <div className={styles.footer}>
          <button type="button" className={styles.ghostButton} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.actionButton}
            onClick={onConfirm}
            disabled={busy || hasConflicts}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
